import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Session } from './utils/session';
import { toDisplayDate } from '../utils/dateOnly';
import { formatIstDateTime, isSameIstDay } from '../utils/istTime';
import { formatTaskMinutes, sumTaskMinutesForUser } from '../utils/taskTime';
import './TaskBoard/TaskBoard.css';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${Session.getToken()}`,
});

function formatLogDateTime(value) {
  return formatIstDateTime(value);
}

function isSameLocalDay(value, referenceDate = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(d.getTime()) || Number.isNaN(ref.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear()
    && d.getMonth() === ref.getMonth()
    && d.getDate() === ref.getDate()
  );
}

function hasLogoutTime(value) {
  if (value == null || value === '') return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime());
}

export default function UserLoginHistoryModal({ userId, userName, startDate, endDate, onClose }) {
  const [loginLogs, setLoginLogs] = useState([]);
  const [taskTimeLabel, setTaskTimeLabel] = useState('0 min');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!userId || !startDate || !endDate) return;

    setLoginLogs([]);
    setTaskTimeLabel('0 min');
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        userId: String(userId),
        fromDate: startDate,
        toDate: endDate,
      });

      const [logRes, startStopRes, userLogsRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/Auth/userlog?${params.toString()}`,
          { method: 'GET', headers: authHeaders() }
        ),
        fetch(`${API_BASE_URL}/api/StartStop/Start_Stop_ALL`, {
          method: 'GET',
          headers: authHeaders(),
        }).catch(() => null),
        fetch(
          `${API_BASE_URL}/api/Auth/userlogs?fromDate=${encodeURIComponent(startDate)}&toDate=${encodeURIComponent(endDate)}`,
          { method: 'GET', headers: authHeaders() }
        ).catch(() => null),
      ]);

      if (!logRes.ok) {
        let message = `Failed to load login history (HTTP ${logRes.status})`;
        try {
          const errBody = await logRes.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      const data = await logRes.json();
      setLoginLogs(Array.isArray(data) ? data : []);

      if (startStopRes?.ok) {
        const startStopData = await startStopRes.json();
        let loginLogs = [];
        if (userLogsRes?.ok) {
          const userLogsData = await userLogsRes.json();
          loginLogs = (Array.isArray(userLogsData) ? userLogsData : [])
            .filter((log) => Number(log.userId ?? log.UserId) === Number(userId));
        }
        const totalMins = sumTaskMinutesForUser(
          Array.isArray(startStopData) ? startStopData : [],
          userId,
          startDate,
          endDate,
          Date.now(),
          loginLogs
        );
        setTaskTimeLabel(formatTaskMinutes(totalMins));
      } else {
        setTaskTimeLabel('0 min');
      }
    } catch (err) {
      setError(err.message || 'Failed to load login history.');
      setLoginLogs([]);
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, endDate]);

  useEffect(() => {
    if (userId) fetchLogs();
  }, [userId, fetchLogs]);

  if (!userId) return null;

  return (
    <div
      className="task-reports-login-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-login-history-modal-title"
      onClick={onClose}
    >
      <div
        className="task-reports-login-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="task-reports-login-modal-header">
          <h4 id="user-login-history-modal-title">{userName || `User ${userId}`}</h4>
          <button
            type="button"
            className="task-reports-login-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p className="task-reports-login-modal-range text-muted small mb-1">
          {toDisplayDate(startDate)} — {toDisplayDate(endDate)}
        </p>
        <p className="task-reports-login-modal-task-time mb-3">
          <strong>Task Time:</strong> {loading ? '...' : taskTimeLabel}
        </p>

        {loading ? (
          <div className="text-center text-muted py-3">Loading login history...</div>
        ) : error ? (
          <div className="text-danger py-2">{error}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead>
                <tr>
                  <th>Login Time</th>
                  <th>Logout Time</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-center text-muted">
                      No login records for this date range.
                    </td>
                  </tr>
                ) : (
                  loginLogs.map((log) => {
                    const loginTime = log.userLoginDate ?? log.UserLoginDate;
                    const logoutTime = log.userLogoutDate ?? log.UserLogoutDate;
                    return (
                      <tr key={log.userLogId ?? log.UserLogId}>
                        <td>{formatLogDateTime(loginTime)}</td>
                        <td>
                          {hasLogoutTime(logoutTime)
                            ? formatLogDateTime(logoutTime)
                            : (loginTime && isSameLocalDay(loginTime, new Date())
                              ? <span className="text-success">Active</span>
                              : '')}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
